import {
  IOsiCorrectionWidgetViewModel,
  IOsiCorrectionWidgetViewModelToken
} from '@shared/types/mobx/widgets/OsiCorrection';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { useMemo } from 'react';
import { tokens, useTranslation } from '@shared/utils/i18n';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import { Box, Button, TextField } from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { DataGridPro, GridColDef, GridValueGetterParams } from '@mui/x-data-grid-pro';
import DataGridToolBar from '@shared/common/DataGridToolBar';
import { OsiCreateCorrectionButton } from '@features/osi/correction/createCorrection';
import { Trans } from 'react-i18next';

export const OsiCorrectionWidget: React.FC = observer(() => {
  const vm = useInjection<IOsiCorrectionWidgetViewModel>(IOsiCorrectionWidgetViewModelToken);

  const { translateToken: tt, getGridLocale } = useTranslation();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'dt',
        headerName: tt(tokens.osiPayments.table.date),
        minWidth: 100,
        type: 'date',
        flex: 0.2,
        valueFormatter: (params: GridValueGetterParams) => new Date(params.value).toLocaleString('ru-RU')
      },
      {
        field: 'abonentName',
        headerName: tt(tokens.osiPayments.table.abonentName),
        minWidth: 150,
        flex: 0.3
      },
      {
        field: 'flat',
        headerName: tt(tokens.osiPayments.table.number),
        sortComparator: flatComparator,
        minWidth: 100,
        flex: 0.2,
        // Фаза 2: см. коммент в OsiPaymentsWidget — flatDisplay от backend, безусловный fallback безопасен
        // (DTO фикс-транзакции без areaTypeCode; для не-арендаторов flatDisplay == сырому flat).
        valueGetter: (params: GridValueGetterParams) => params.row.flatDisplay ?? params.row.flat
      },
      { field: 'serviceName', headerName: tt(tokens.osiPayments.table.service), minWidth: 150, flex: 0.3 },
      { field: 'reason', headerName: tt(tokens.osiPayments.table.reason), minWidth: 100, flex: 0.3 },
      {
        field: 'amount',
        headerName: tt(tokens.osiPayments.table.amount),
        minWidth: 150,
        flex: 0.2,
        align: 'left',
        type: 'number',
        valueGetter: (params: GridValueGetterParams) => params.value.toFixed(2)
      }
    ],
    [tt]
  );

  return (
    <>
      <Box>
        <OsiCreateCorrectionButton onSuccess={vm.loadFixes} />
      </Box>
      <Box display="flex" alignItems="center">
        <DatePicker
          views={['year', 'month']}
          openTo={'month'}
          disabled={vm.isLoading}
          onAccept={(date: Date | null) => {
            vm.date = date;
          }}
          label={tt(tokens.common.formFields.month.label)}
          minDate={new Date('2021-03-01')}
          maxDate={new Date('2031-03-01')}
          value={vm.date}
          onChange={() => null}
          renderInput={(params) => <TextField size="small" {...params} helperText={null} />}
        />

        <Button
          disabled={vm.isLoading}
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={async () => {
            await vm.loadFixes();
          }}
          sx={{ ml: 1, mr: 1 }}
          size="medium"
        >
          <Trans i18nKey={'common:refresh'} />
        </Button>
        <Button
          disabled={vm.isLoading}
          variant="outlined"
          color="primary"
          startIcon={<CloudDownloadIcon />}
          onClick={vm.downloadReport}
          size="medium"
        >
          <Trans i18nKey={'common:download'} />
        </Button>
      </Box>
      <Box sx={{ height: '62vh' }}>
        <DataGridPro
          loading={vm.isLoading}
          localeText={getGridLocale()}
          rows={vm.rows}
          columns={columns}
          density="compact"
          disableSelectionOnClick
          components={{
            Toolbar: DataGridToolBar
          }}
        />
      </Box>
    </>
  );
});
